import { useCallback, useState } from "react";
import { Button } from "@radix-ui/themes";
import { Dialog } from "../../components/shared/dialog";
import styles from "./Members.module.scss";
import Select from "../../components/shared/select";
import { useCreateMembers } from "../../api/member/Member.api";
import {
  Permission,
  PermissionDisplay,
  Role,
} from "../../api/member/Member.model";
import { toast } from "sonner";

import { useGetTenants } from "../../api/tenants";
import { Checkbox } from "@geist-ui/core";
import { PlusIcon } from "@radix-ui/react-icons";
import MultiSelect from "../../components/shared/multi-select/Select";

export function AddMembersDialog({
  refetch,
}: {
  refetch: () => Promise<unknown>;
}) {
  const { data: tenants } = useGetTenants();

  const { mutateAsync: createMembers, isLoading } = useCreateMembers();

  const [open, setOpen] = useState<boolean>(false);
  const [roleValue, setRoleValue] = useState<Role>(Role.AGENT);
  const [allowedPermissions, setAllowedPermissions] = useState<Permission[]>(
    []
  );
  const [allowedTenants, setAllowedTenants] = useState<string[]>([]);
  const [email, setEmail] = useState<string>("");

  const rolesOptions = [
    { label: "Agent - Copilot AI assistant access", value: Role.AGENT },
    { label: "Member - Customized platform access", value: Role.MEMBER },
    { label: "Admin - Full administrative access", value: Role.ADMIN },
  ];

  const permissionsOptions = Object.entries(PermissionDisplay)
    .map((p) => {
      return {
        value: p[0] as Permission,
        label: p[1],
      };
    })
    .filter((p) => p.value !== Permission.COPILOT);

  const validateEmail = (): boolean => {
    const regexPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return email.trim().length > 0 && regexPattern.test(email.trim());
  };

  const closeDialog = () => {
    setOpen(false);
    setRoleValue(Role.AGENT);
    setAllowedPermissions([]);
    setAllowedTenants([]);
    setEmail("");
  };

  const handleAddNew = useCallback(async () => {
    const isAdmin = roleValue === Role.ADMIN;
    const isAgent = roleValue === Role.AGENT;

    await createMembers(
      {
        emails: [email],
        permissions: isAgent
          ? [Permission.COPILOT]
          : [...allowedPermissions, Permission.COPILOT],
        tenantsIds: isAgent ? tenants?.map((t) => t.id) : allowedTenants,
        isAdmin,
      },
      {
        onSettled: async () => {
          await refetch();
          closeDialog();
          toast("Member added");
        },
      }
    );
  }, [
    email,
    roleValue,
    allowedPermissions,
    allowedTenants,
    createMembers,
    refetch,
  ]);

  return (
    <>
      <span>
        <Button
          color="gray"
          variant="solid"
          highContrast
          onClick={() => setOpen(true)}
        >
          <PlusIcon />
          Invite Member
        </Button>
      </span>

      <Dialog open={open} onOpenChange={setOpen}>
        <Dialog.Content size="large" id="dialog-members">
          <div className={styles.AddMember}>
            <Dialog.Close className={styles.Close} />

            <div className={styles.Header}>
              <div className={styles.Title}>Invite member</div>
            </div>
            <div className={styles.Content}>
              <div className={styles.AccessParameter}>
                <div className={styles.Option}>
                  <div className={styles.Title}>Email</div>
                </div>

                <input
                  type="email"
                  placeholder="Add member by email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className={styles.AccessParameter}>
                <div className={styles.Option}>
                  <div className={styles.Title}>Role</div>
                </div>

                <Select
                  options={rolesOptions}
                  value={roleValue}
                  onChange={(newValue) => {
                    setRoleValue(newValue as Role);
                  }}
                  classes={{ trigger: styles.Select }}
                />
              </div>

              {roleValue === Role.MEMBER ? (
                <div className={styles.AccessParameter}>
                  <div className={styles.Option}>
                    <div className={styles.Title}>Platform access</div>
                  </div>

                  <div className={styles.ChoiceList}>
                    {permissionsOptions.map((permission) => (
                      <Checkbox
                        className={styles.ChoiceItem}
                        key={permission.value}
                        scale={2}
                        checked={allowedPermissions.includes(permission.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAllowedPermissions((prev) => [
                              ...prev,
                              permission.value,
                            ]);
                          } else {
                            setAllowedPermissions((prev) =>
                              prev.filter((p) => p !== permission.value)
                            );
                          }
                        }}
                      >
                        <span>{permission.label}</span>
                      </Checkbox>
                    ))}
                  </div>
                </div>
              ) : (
                <></>
              )}

              {roleValue === Role.MEMBER ? (
                <div className={styles.AccessParameter}>
                  <div className={styles.Option}>
                    <div className={styles.Title}>Spaces access</div>
                  </div>

                  <MultiSelect
                    options={
                      tenants?.map((option) => ({
                        label: option.id,
                        value: option.id,
                      })) || []
                    }
                    value={allowedTenants}
                    onChange={setAllowedTenants}
                    classes={{ trigger: styles.MultiSelect }}
                  />
                </div>
              ) : (
                <></>
              )}
            </div>

            <div className={styles.Footer}>
              <Button
                color="gray"
                variant="solid"
                highContrast
                onClick={handleAddNew}
                loading={false}
                disabled={!validateEmail() || isLoading}
                className={styles.AddButton}
              >
                {isLoading ? "..." : "Send Invites"}
              </Button>
              <Button
                color="gray"
                variant="ghost"
                highContrast
                onClick={() => {
                  closeDialog();
                }}
                className={styles.CancelButton}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
}
